import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { CompiledProjectTrack, ProjectTrackSpan } from '../app/ride-track.ts';
import type { SimulationFrame } from '../physics/index.ts';
import type { CoasterMaterialSet } from '../scene/index.ts';
import type { CoasterProject } from '../schema/index.ts';
import type { SemanticSectionType, TrackSampler } from '../track/index.ts';
import { getAuthoredDescriptor } from '../builder-v2/catalog.ts';

const RAIL_OFFSET = 0.68;
const RAIL_RADIUS = 0.13;
const RAIL_SAMPLE_SPACING = 0.45;
const TIE_SPACING = 1.05;
const SUPPORT_SPACING = 8;
const INVERSION_SUPPORT_CLEARANCE = 1.2;
const MAX_SEAM_GAP_METERS = 0.00001;
const MAX_SEAM_ANGLE_DEGREES = 0.1;

type RailSide = 'left' | 'right';

export interface TrackContinuityMetrics {
  readonly maxRailGapMeters: number;
  readonly maxRailNormalAngleDegrees: number;
  readonly maxTieSpacingMeters: number;
}

export interface TrackVisualQuality {
  readonly railRadialSegments: number;
  readonly supportRadialSegments: number;
  readonly castSmallDetailShadows: boolean;
}

const DEFAULT_TRACK_VISUAL_QUALITY: TrackVisualQuality = Object.freeze({
  railRadialSegments: 8,
  supportRadialSegments: 8,
  castSmallDetailShadows: true,
});

export interface SectionPickTarget {
  readonly sectionId: string;
  readonly points: readonly THREE.Vector3[];
}

interface RailBoundary {
  readonly positions: readonly THREE.Vector3[];
  readonly normals: readonly THREE.Vector3[];
}

interface RailBuild {
  readonly mesh: THREE.Mesh;
  readonly start: RailBoundary;
  readonly end: RailBoundary;
}

interface TrackChunkBuild {
  readonly root: THREE.Group;
  readonly rails: Readonly<Record<RailSide, RailBuild>>;
  readonly tieDistances: readonly number[];
}

interface InstanceBatch {
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.Material;
  readonly matrices: THREE.Matrix4[];
  readonly name: string;
  readonly proceduralSectionHardware: boolean;
}

export interface TrackSupportContext {
  readonly inversionSectionIds: ReadonlySet<string>;
  readonly inversionSupportSectionIds: ReadonlySet<string>;
}

export interface SupportBeamDescriptor {
  readonly start: THREE.Vector3;
  readonly end: THREE.Vector3;
  readonly topRadius: number;
  readonly bottomRadius: number;
}

const EMPTY_SUPPORT_CONTEXT: TrackSupportContext = {
  inversionSectionIds: new Set<string>(),
  inversionSupportSectionIds: new Set<string>(),
};

const INVERSION_PIECE_DEFINITIONS = new Set([
  'loop',
  'loop_left',
  'loop_right',
  'roll_left',
  'roll_right',
  'corkscrew_left',
  'corkscrew_right',
]);

export function createTrackSupportContext(project: CoasterProject): TrackSupportContext {
  const inversionSectionIds = new Set<string>();
  const inversionSupportSectionIds = new Set<string>();
  const sectionById = new Map(project.track.sections.map((section) => [section.id, section]));
  const nodeById = new Map(project.track.nodes.map((node) => [node.id, node]));
  const pieceRecords =
    project.track.geometryKind === 'authored_grid_v2'
      ? project.track.pieces.map((piece) => ({
          definitionId: piece.descriptorId,
          sectionIds: piece.sectionIds,
          inversion: getAuthoredDescriptor(piece.descriptorId)?.supportStyle === 'sparse_inversion',
        }))
      : project.editor.pieceGroups.map((piece) => ({
          definitionId: piece.definitionId,
          sectionIds: piece.sectionIds,
          inversion: INVERSION_PIECE_DEFINITIONS.has(piece.definitionId),
        }));
  for (const piece of pieceRecords) {
    if (!piece.inversion || piece.sectionIds.length === 0) continue;
    for (const sectionId of piece.sectionIds) inversionSectionIds.add(sectionId);
    const pieceSections = piece.sectionIds
      .map((sectionId) => sectionById.get(sectionId))
      .filter((section) => section !== undefined);
    const sectionMidpointHeights = pieceSections.map((section) => {
      const from = nodeById.get(section.fromNodeId);
      const to = nodeById.get(section.toNodeId);
      return from && to ? (from.position.y + to.position.y) * 0.5 : 0;
    });
    const lowestHeight = Math.min(...sectionMidpointHeights);
    const highestHeight = Math.max(...sectionMidpointHeights);
    const targetHeight = lowestHeight + (highestHeight - lowestHeight) * 0.5;
    const midpoint = Math.ceil(pieceSections.length / 2);
    const anchorIndices = [
      { start: 0, end: midpoint },
      { start: midpoint, end: pieceSections.length },
    ].map(({ start, end }) => {
      let bestIndex = start;
      let bestGap = Number.POSITIVE_INFINITY;
      for (let index = start; index < end; index += 1) {
        const gap = Math.abs((sectionMidpointHeights[index] ?? 0) - targetHeight);
        if (gap < bestGap) {
          bestGap = gap;
          bestIndex = index;
        }
      }
      return bestIndex;
    });
    for (const index of anchorIndices) {
      const sectionId = pieceSections[index]?.id;
      if (sectionId) inversionSupportSectionIds.add(sectionId);
    }
  }
  return { inversionSectionIds, inversionSupportSectionIds };
}

function vector(value: {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}): THREE.Vector3 {
  return new THREE.Vector3(value.x, value.y, value.z);
}

function disposeGroup(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (object instanceof THREE.Mesh || object instanceof THREE.InstancedMesh)
      object.geometry.dispose();
  });
}

export function batchStaticMeshHierarchy(
  root: THREE.Object3D,
  classify: (mesh: THREE.Mesh) => string = () => 'default',
): THREE.Group {
  root.updateMatrixWorld(true);
  const rootInverse = root.matrixWorld.clone().invert();
  const groups = new Map<
    string,
    {
      geometries: THREE.BufferGeometry[];
      material: THREE.Material;
      label: string;
      castShadow: boolean;
      receiveShadow: boolean;
      visible: boolean;
    }
  >();
  const extractRange = (
    geometry: THREE.BufferGeometry,
    start: number,
    count: number,
  ): THREE.BufferGeometry => {
    const extracted = new THREE.BufferGeometry();
    for (const [name, attribute] of Object.entries(geometry.attributes)) {
      const values: number[] = [];
      for (let offset = 0; offset < count; offset += 1) {
        const vertex = geometry.index?.getX(start + offset) ?? start + offset;
        values.push(attribute.getX(vertex));
        if (attribute.itemSize > 1) values.push(attribute.getY(vertex));
        if (attribute.itemSize > 2) values.push(attribute.getZ(vertex));
        if (attribute.itemSize > 3) values.push(attribute.getW(vertex));
      }
      extracted.setAttribute(
        name,
        new THREE.Float32BufferAttribute(values, attribute.itemSize, attribute.normalized),
      );
    }
    return extracted;
  };
  const addGeometry = (
    object: THREE.Mesh,
    material: THREE.Material,
    geometry: THREE.BufferGeometry,
    label: string,
  ) => {
    const attributes = Object.keys(geometry.attributes).sort().join(',');
    const materialKey = (material.name || material.type).replace(/\.\d+$/, '');
    const key = `${label}:${materialKey}:${attributes}:${Boolean(geometry.index)}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        geometries: [],
        material,
        label,
        castShadow: object.castShadow,
        receiveShadow: object.receiveShadow,
        visible: object.visible,
      };
      groups.set(key, group);
    } else {
      group.castShadow ||= object.castShadow;
      group.receiveShadow ||= object.receiveShadow;
    }
    const localMatrix = rootInverse.clone().multiply(object.matrixWorld);
    group.geometries.push(geometry.applyMatrix4(localMatrix));
  };
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || object instanceof THREE.InstancedMesh) return;
    const lodMatch = /__LOD([012])$/.exec(object.name);
    if (!object.visible && !lodMatch) return;
    for (let parent = object.parent; parent && parent !== root; parent = parent.parent)
      if (!parent.visible && !lodMatch) return;
    const label = `${classify(object)}${lodMatch ? `__LOD${lodMatch[1]}` : ''}`;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    if (materials.length === 1) addGeometry(object, materials[0]!, object.geometry.clone(), label);
    else {
      const ranges = object.geometry.groups.length
        ? object.geometry.groups
        : [
            {
              start: 0,
              count: object.geometry.index?.count ?? object.geometry.getAttribute('position').count,
              materialIndex: 0,
            },
          ];
      for (const range of ranges) {
        const material = materials[range.materialIndex ?? 0];
        if (material)
          addGeometry(
            object,
            material,
            extractRange(object.geometry, range.start, range.count),
            label,
          );
      }
    }
    object.visible = false;
    if (lodMatch) object.name = `${object.name}::batched-source`;
  });
  const batchRoot = new THREE.Group();
  batchRoot.name = 'production-static-batches';
  batchRoot.userData.ownedBatchGeometry = true;
  for (const group of groups.values()) {
    const geometry = mergeGeometries(group.geometries, false);
    group.geometries.forEach((entry) => entry.dispose());
    if (!geometry) continue;
    const mesh = new THREE.Mesh(geometry, group.material);
    mesh.name = `production-batch:${group.label}`;
    mesh.castShadow = group.castShadow;
    mesh.receiveShadow = group.receiveShadow;
    mesh.visible = group.visible;
    mesh.raycast = () => undefined;
    batchRoot.add(mesh);
  }
  root.add(batchRoot);
  return batchRoot;
}

export function disposeStaticMeshBatches(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (object.parent?.userData.ownedBatchGeometry === true && object instanceof THREE.Mesh)
      object.geometry.dispose();
  });
}

export function batchStaticInstancedHierarchy(
  root: THREE.Object3D,
  exclude: (mesh: THREE.InstancedMesh) => boolean,
): THREE.Group {
  const groups = new Map<
    string,
    { geometries: THREE.BufferGeometry[]; material: THREE.Material; castShadow: boolean }
  >();
  const instanceMatrix = new THREE.Matrix4();
  root.traverse((object) => {
    if (!(object instanceof THREE.InstancedMesh) || !object.visible || exclude(object)) return;
    const material = Array.isArray(object.material) ? object.material[0] : object.material;
    if (!(material instanceof THREE.Material)) return;
    const attributes = Object.keys(object.geometry.attributes).sort().join(',');
    const materialKey = (material.name || material.type).replace(/\.\d+$/, '');
    const key = `${materialKey}:${attributes}:${Boolean(object.geometry.index)}:${object.castShadow}`;
    let group = groups.get(key);
    if (!group) {
      group = { geometries: [], material, castShadow: object.castShadow };
      groups.set(key, group);
    }
    for (let index = 0; index < object.count; index += 1) {
      object.getMatrixAt(index, instanceMatrix);
      group.geometries.push(object.geometry.clone().applyMatrix4(instanceMatrix));
    }
    object.visible = false;
  });
  const batchRoot = new THREE.Group();
  batchRoot.name = 'production-static-batches';
  batchRoot.userData.ownedBatchGeometry = true;
  for (const group of groups.values()) {
    const geometry = mergeGeometries(group.geometries, false);
    group.geometries.forEach((entry) => entry.dispose());
    if (!geometry) continue;
    const mesh = new THREE.Mesh(geometry, group.material);
    mesh.name = 'production-batch:hardware';
    mesh.castShadow = group.castShadow;
    mesh.receiveShadow = true;
    mesh.raycast = () => undefined;
    batchRoot.add(mesh);
  }
  root.add(batchRoot);
  return batchRoot;
}

function frameMatrix(
  sampler: TrackSampler,
  distance: number,
  target: THREE.Matrix4,
): THREE.Matrix4 {
  const sample = sampler.sampleByDistance(distance);
  const right = vector(sample.right);
  const up = vector(sample.up);
  const tangent = vector(sample.tangent);
  return target.makeBasis(right, up, tangent).setPosition(vector(sample.position));
}

function railBoundary(
  positions: readonly number[],
  normals: readonly number[],
  ringIndex: number,
  radialSegments: number,
): RailBoundary {
  const ringOffset = ringIndex * radialSegments * 3;
  return {
    positions: Array.from({ length: radialSegments }, (_, index) => {
      const offset = ringOffset + index * 3;
      return new THREE.Vector3(positions[offset], positions[offset + 1], positions[offset + 2]);
    }),
    normals: Array.from({ length: radialSegments }, (_, index) => {
      const offset = ringOffset + index * 3;
      return new THREE.Vector3(normals[offset], normals[offset + 1], normals[offset + 2]);
    }),
  };
}

function makeRail(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  side: number,
  material: THREE.Material,
  radialSegments: number,
): RailBuild {
  const segmentCount = Math.max(
    5,
    Math.ceil((span.endDistance - span.startDistance) / RAIL_SAMPLE_SPACING),
  );
  const ringCount = segmentCount + 1;
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let ringIndex = 0; ringIndex < ringCount; ringIndex += 1) {
    const distance =
      span.startDistance + ((span.endDistance - span.startDistance) * ringIndex) / segmentCount;
    const sample = sampler.sampleByDistance(distance);
    const trackRight = vector(sample.right).normalize();
    const trackUp = vector(sample.up).normalize();
    const center = vector(sample.position).addScaledVector(trackRight, side * RAIL_OFFSET);
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      const angle = (radialIndex / radialSegments) * Math.PI * 2;
      const normal = trackUp
        .clone()
        .multiplyScalar(Math.cos(angle))
        .addScaledVector(trackRight, Math.sin(angle))
        .normalize();
      const vertex = center.clone().addScaledVector(normal, RAIL_RADIUS);
      positions.push(vertex.x, vertex.y, vertex.z);
      normals.push(normal.x, normal.y, normal.z);
      uvs.push(radialIndex / radialSegments, ringIndex / segmentCount);
    }
  }

  for (let ringIndex = 0; ringIndex < segmentCount; ringIndex += 1) {
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      const nextRadial = (radialIndex + 1) % radialSegments;
      const current = ringIndex * radialSegments + radialIndex;
      const currentNext = ringIndex * radialSegments + nextRadial;
      const following = (ringIndex + 1) * radialSegments + radialIndex;
      const followingNext = (ringIndex + 1) * radialSegments + nextRadial;
      indices.push(current, following, currentNext, currentNext, following, followingNext);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return {
    mesh,
    start: railBoundary(positions, normals, 0, radialSegments),
    end: railBoundary(positions, normals, ringCount - 1, radialSegments),
  };
}

function makeTies(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
  sectionType: SemanticSectionType,
): { mesh: THREE.InstancedMesh; distances: number[] } {
  const spanLength = span.endDistance - span.startDistance;
  const count = Math.max(1, Math.ceil(spanLength / TIE_SPACING));
  const spacing = spanLength / count;
  const geometry = new THREE.BoxGeometry(1.75, 0.12, 0.18);
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  const matrix = new THREE.Matrix4();
  const distances = Array.from(
    { length: count },
    (_, index) => span.startDistance + index * spacing,
  );
  distances.forEach((distance, index) =>
    mesh.setMatrixAt(index, frameMatrix(sampler, distance, matrix)),
  );
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.name = `track-ties:${sectionType}`;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return { mesh, distances };
}

function makeBeam(
  descriptor: SupportBeamDescriptor,
  material: THREE.Material,
  radialSegments: number,
): THREE.Mesh {
  const direction = descriptor.end.clone().sub(descriptor.start);
  const length = direction.length();
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(
      descriptor.topRadius,
      descriptor.bottomRadius,
      length,
      radialSegments,
    ),
    material,
  );
  beam.position.copy(descriptor.start).add(descriptor.end).multiplyScalar(0.5);
  beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  beam.castShadow = true;
  beam.receiveShadow = true;
  return beam;
}

export function inversionSupportLayout(
  position: THREE.Vector3,
  groundY = 0,
): readonly SupportBeamDescriptor[] {
  const start = new THREE.Vector3(position.x, position.y - 0.3, position.z);
  return [
    {
      start,
      end: new THREE.Vector3(start.x, groundY + 0.12, start.z),
      topRadius: 0.14,
      bottomRadius: 0.23,
    },
  ];
}

function supportClearsTrack(
  sampler: TrackSampler,
  beam: SupportBeamDescriptor,
  attachmentDistance: number,
): boolean {
  const step = Math.max(0.75, sampler.length / 800);
  for (let distance = 0; distance < sampler.length; distance += step) {
    const directGap = Math.abs(distance - attachmentDistance);
    const alongTrackGap = sampler.closed
      ? Math.min(directGap, sampler.length - directGap)
      : directGap;
    if (alongTrackGap < 4) continue;
    const sample = sampler.sampleByDistance(distance).position;
    if (sample.y < beam.end.y - 0.5 || sample.y > beam.start.y + 0.75) continue;
    const horizontalGap = Math.hypot(sample.x - beam.start.x, sample.z - beam.start.z);
    if (horizontalGap < INVERSION_SUPPORT_CLEARANCE) return false;
  }
  return true;
}

function makeSupports(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
  inversion: boolean,
  inversionSupport: boolean,
  radialSegments: number,
): THREE.Group {
  const root = new THREE.Group();
  root.name = 'track-supports';
  const supportDistances: number[] = [];
  root.userData.productionSupportDistances = supportDistances;
  const spanLength = span.endDistance - span.startDistance;
  if (inversion) {
    if (!inversionSupport) return root;
    const candidateDistances = [0.5, 0.35, 0.65, 0.2, 0.8].map(
      (fraction) => span.startDistance + spanLength * fraction,
    );
    for (const distance of candidateDistances) {
      const sample = sampler.sampleByDistance(distance);
      if (sample.position.y < 1.5) continue;
      const [beam] = inversionSupportLayout(vector(sample.position));
      if (!beam || !supportClearsTrack(sampler, beam, distance)) continue;
      const mesh = makeBeam(beam, material, radialSegments);
      mesh.name = 'inversion-support';
      root.add(mesh);
      supportDistances.push(distance);
      break;
    }
    return root;
  }
  const count = Math.min(4, Math.max(1, Math.floor(spanLength / SUPPORT_SPACING)));
  for (let index = 0; index < count; index += 1) {
    const distance = span.startDistance + ((index + 0.5) / count) * spanLength;
    const sample = sampler.sampleByDistance(distance);
    if (sample.position.y < 1.5) continue;
    const supportBeam: SupportBeamDescriptor = {
      start: new THREE.Vector3(sample.position.x, sample.position.y - 0.2, sample.position.z),
      end: new THREE.Vector3(sample.position.x, 0.12, sample.position.z),
      topRadius: 0.15,
      bottomRadius: 0.24,
    };
    if (!supportClearsTrack(sampler, supportBeam, distance)) continue;
    const height = Math.max(0.5, sample.position.y - 0.2);
    const column = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.24, height, radialSegments),
      material,
    );
    column.position.set(sample.position.x, height / 2, sample.position.z);
    column.castShadow = true;
    column.receiveShadow = true;
    const cap = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.16, 0.16), material);
    cap.position.set(sample.position.x, sample.position.y - 0.28, sample.position.z);
    cap.quaternion.setFromRotationMatrix(frameMatrix(sampler, distance, new THREE.Matrix4()));
    cap.castShadow = true;
    root.add(column, cap);
    supportDistances.push(distance);
  }
  return root;
}

function hardwareDistances(span: ProjectTrackSpan, targetSpacing: number): number[] {
  const spanLength = span.endDistance - span.startDistance;
  const count = Math.max(1, Math.ceil(spanLength / targetSpacing));
  return Array.from(
    { length: count },
    (_, index) => span.startDistance + ((index + 0.5) / count) * spanLength,
  );
}

function hardwareMatrix(
  sampler: TrackSampler,
  distance: number,
  localTransform: THREE.Matrix4,
  target: THREE.Matrix4,
): THREE.Matrix4 {
  return frameMatrix(sampler, distance, target).multiply(localTransform);
}

function makeLiftHardware(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
  castShadow: boolean,
): THREE.InstancedMesh {
  const distances = hardwareDistances(span, 0.78);
  const geometry = new THREE.TorusGeometry(0.22, 0.055, 5, 10);
  geometry.scale(0.78, 1.3, 1);
  const mesh = new THREE.InstancedMesh(geometry, material, distances.length);
  const matrix = new THREE.Matrix4();
  distances.forEach((distance, index) => {
    const local = new THREE.Matrix4().makeTranslation(0, 0.2, 0);
    if (index % 2) local.multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
    mesh.setMatrixAt(index, hardwareMatrix(sampler, distance, local, matrix));
  });
  mesh.name = 'lift-chain';
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.castShadow = castShadow;
  return mesh;
}

function makeLaunchHardware(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
  castShadow: boolean,
): THREE.InstancedMesh {
  const distances = hardwareDistances(span, 1.2);
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.18, 0.42, 0.62),
    material,
    distances.length,
  );
  const matrix = new THREE.Matrix4();
  const local = new THREE.Matrix4().makeTranslation(0, 0.23, 0);
  distances.forEach((distance, index) =>
    mesh.setMatrixAt(index, hardwareMatrix(sampler, distance, local, matrix)),
  );
  mesh.name = 'launch-fins';
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.castShadow = castShadow;
  return mesh;
}

function makeBrakeHardware(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
  castShadow: boolean,
): THREE.InstancedMesh {
  const distances = hardwareDistances(span, 1);
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.12, 0.5, 0.68),
    material,
    distances.length * 2,
  );
  const matrix = new THREE.Matrix4();
  distances.forEach((distance, index) => {
    for (const [sideIndex, side] of [-0.28, 0.28].entries()) {
      const local = new THREE.Matrix4().makeTranslation(side, 0.25, 0);
      mesh.setMatrixAt(index * 2 + sideIndex, hardwareMatrix(sampler, distance, local, matrix));
    }
  });
  mesh.name = 'brake-fins';
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.castShadow = castShadow;
  return mesh;
}

function makeStationHardware(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  material: THREE.Material,
): THREE.InstancedMesh {
  const distances = hardwareDistances(span, 1.35);
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1.15, 0.16, 1.2),
    material,
    distances.length * 2,
  );
  const matrix = new THREE.Matrix4();
  distances.forEach((distance, index) => {
    for (const [sideIndex, side] of [-1.45, 1.45].entries()) {
      const local = new THREE.Matrix4().makeTranslation(side, -0.08, 0);
      mesh.setMatrixAt(index * 2 + sideIndex, hardwareMatrix(sampler, distance, local, matrix));
    }
  });
  mesh.name = 'station-platform';
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.receiveShadow = true;
  return mesh;
}

function makeSectionHardware(
  sampler: TrackSampler,
  span: ProjectTrackSpan,
  materials: CoasterMaterialSet,
  castSmallDetailShadows: boolean,
): THREE.Object3D | null {
  const midpoint = span.startDistance + (span.endDistance - span.startDistance) * 0.5;
  switch (sampler.sampleByDistance(midpoint).section?.type) {
    case 'lift':
      return makeLiftHardware(sampler, span, materials.chain, castSmallDetailShadows);
    case 'launch':
      return makeLaunchHardware(sampler, span, materials.launch, castSmallDetailShadows);
    case 'brake':
      return makeBrakeHardware(sampler, span, materials.brake, castSmallDetailShadows);
    case 'station':
      return makeStationHardware(sampler, span, materials.station);
    default:
      return null;
  }
}

function sectionTypeForSpan(sampler: TrackSampler, span: ProjectTrackSpan): SemanticSectionType {
  const midpoint = span.startDistance + (span.endDistance - span.startDistance) * 0.5;
  return sampler.sampleByDistance(midpoint).section?.type ?? 'normal';
}

function tieMaterialForSection(
  sectionType: SemanticSectionType,
  materials: CoasterMaterialSet,
): THREE.Material {
  switch (sectionType) {
    case 'station':
      return materials.stationRoof;
    case 'lift':
      return materials.chain;
    case 'launch':
      return materials.launch;
    case 'brake':
      return materials.brake;
    default:
      return materials.tie;
  }
}

function makeChunk(
  plan: CompiledProjectTrack,
  span: ProjectTrackSpan,
  materials: CoasterMaterialSet,
  supportContext: TrackSupportContext,
  quality: TrackVisualQuality,
): TrackChunkBuild {
  const sectionType = sectionTypeForSpan(plan.sampler, span);
  const left = makeRail(plan.sampler, span, -1, materials.track, quality.railRadialSegments);
  const right = makeRail(plan.sampler, span, 1, materials.track, quality.railRadialSegments);
  const ties = makeTies(
    plan.sampler,
    span,
    tieMaterialForSection(sectionType, materials),
    sectionType,
  );
  const root = new THREE.Group();
  root.name = `track-span:${span.index}`;
  const midpoint = span.startDistance + (span.endDistance - span.startDistance) * 0.5;
  const sectionId = plan.sampler.sampleByDistance(midpoint).section?.id ?? null;
  root.userData.sectionId = sectionId;
  const hardware = makeSectionHardware(
    plan.sampler,
    span,
    materials,
    quality.castSmallDetailShadows,
  );
  root.add(
    left.mesh,
    right.mesh,
    ties.mesh,
    makeSupports(
      plan.sampler,
      span,
      materials.support,
      sectionId !== null && supportContext.inversionSectionIds.has(sectionId),
      sectionId !== null && supportContext.inversionSupportSectionIds.has(sectionId),
      quality.supportRadialSegments,
    ),
  );
  if (hardware) {
    hardware.userData.proceduralSectionHardware = true;
    root.add(hardware);
  }
  root.traverse((object) => {
    object.userData.sectionId = root.userData.sectionId;
  });
  return { root, rails: { left, right }, tieDistances: ties.distances };
}

function createTrackRenderBatches(chunks: readonly TrackChunkBuild[]): THREE.Group {
  const root = new THREE.Group();
  root.name = 'track-render-batches';
  const railGeometries = chunks.flatMap((chunk) => [
    chunk.rails.left.mesh.geometry,
    chunk.rails.right.mesh.geometry,
  ]);
  const railMaterial = chunks[0]?.rails.left.mesh.material;
  const mergedRails = railGeometries.length ? mergeGeometries(railGeometries, false) : null;
  if (mergedRails && railMaterial instanceof THREE.Material) {
    const mesh = new THREE.Mesh(mergedRails, railMaterial);
    mesh.name = 'track-rails-batched';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.raycast = () => undefined;
    root.add(mesh);
  }

  const supportGeometries: THREE.BufferGeometry[] = [];
  let supportMaterial: THREE.Material | null = null;
  const instanceBatches = new Map<string, InstanceBatch>();
  const matrix = new THREE.Matrix4();
  for (const chunk of chunks) {
    chunk.root.traverse((object) => {
      if (object instanceof THREE.Mesh && object.parent?.name === 'track-supports') {
        object.updateMatrix();
        supportGeometries.push(object.geometry.clone().applyMatrix4(object.matrix));
        if (object.material instanceof THREE.Material) supportMaterial ??= object.material;
        return;
      }
      if (!(object instanceof THREE.InstancedMesh)) return;
      const material = Array.isArray(object.material) ? object.material[0] : object.material;
      if (!(material instanceof THREE.Material)) return;
      const proceduralSectionHardware = object.userData.proceduralSectionHardware === true;
      const key = `${object.name}:${material.uuid}:${proceduralSectionHardware}`;
      let batch = instanceBatches.get(key);
      if (!batch) {
        batch = {
          geometry: object.geometry.clone(),
          material,
          matrices: [],
          name: `${object.name}-batched`,
          proceduralSectionHardware,
        };
        instanceBatches.set(key, batch);
      }
      for (let index = 0; index < object.count; index += 1) {
        object.getMatrixAt(index, matrix);
        batch.matrices.push(matrix.clone());
      }
    });
    chunk.root.visible = false;
  }

  const mergedSupports = supportGeometries.length
    ? mergeGeometries(supportGeometries, false)
    : null;
  supportGeometries.forEach((geometry) => geometry.dispose());
  if (mergedSupports && supportMaterial) {
    const mesh = new THREE.Mesh(mergedSupports, supportMaterial);
    mesh.name = 'track-supports-batched';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.raycast = () => undefined;
    root.add(mesh);
  }

  for (const batch of instanceBatches.values()) {
    const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, batch.matrices.length);
    batch.matrices.forEach((instanceMatrix, index) => mesh.setMatrixAt(index, instanceMatrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    mesh.name = batch.name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.raycast = () => undefined;
    mesh.userData.proceduralSectionHardware = batch.proceduralSectionHardware;
    root.add(mesh);
  }
  return root;
}

function boundaryMetrics(
  first: RailBoundary,
  second: RailBoundary,
): {
  gap: number;
  angleDegrees: number;
} {
  let gap = 0;
  let angleDegrees = 0;
  for (
    let index = 0;
    index < Math.min(first.positions.length, second.positions.length);
    index += 1
  ) {
    gap = Math.max(gap, first.positions[index]!.distanceTo(second.positions[index]!));
    const dot = THREE.MathUtils.clamp(first.normals[index]!.dot(second.normals[index]!), -1, 1);
    angleDegrees = Math.max(angleDegrees, THREE.MathUtils.radToDeg(Math.acos(dot)));
  }
  return { gap, angleDegrees };
}

function continuityMetrics(
  plan: CompiledProjectTrack,
  chunks: readonly TrackChunkBuild[],
): TrackContinuityMetrics {
  let maxRailGapMeters = 0;
  let maxRailNormalAngleDegrees = 0;
  const pairCount = plan.sampler.closed ? chunks.length : Math.max(0, chunks.length - 1);
  for (let index = 0; index < pairCount; index += 1) {
    const current = chunks[index]!;
    const next = chunks[(index + 1) % chunks.length]!;
    for (const side of ['left', 'right'] as const) {
      const metrics = boundaryMetrics(current.rails[side].end, next.rails[side].start);
      maxRailGapMeters = Math.max(maxRailGapMeters, metrics.gap);
      maxRailNormalAngleDegrees = Math.max(maxRailNormalAngleDegrees, metrics.angleDegrees);
    }
  }

  const tieDistances = chunks.flatMap((chunk) => chunk.tieDistances).sort((a, b) => a - b);
  let maxTieSpacingMeters = 0;
  for (let index = 1; index < tieDistances.length; index += 1)
    maxTieSpacingMeters = Math.max(
      maxTieSpacingMeters,
      tieDistances[index]! - tieDistances[index - 1]!,
    );
  if (tieDistances.length) {
    maxTieSpacingMeters = Math.max(maxTieSpacingMeters, tieDistances[0]!);
    const finalGap = plan.sampler.closed
      ? plan.sampler.length - tieDistances.at(-1)! + tieDistances[0]!
      : plan.sampler.length - tieDistances.at(-1)!;
    maxTieSpacingMeters = Math.max(maxTieSpacingMeters, finalGap);
  }
  return { maxRailGapMeters, maxRailNormalAngleDegrees, maxTieSpacingMeters };
}

function assertContinuity(metrics: TrackContinuityMetrics): void {
  if (
    !Object.values(metrics).every(Number.isFinite) ||
    metrics.maxRailGapMeters > MAX_SEAM_GAP_METERS ||
    metrics.maxRailNormalAngleDegrees > MAX_SEAM_ANGLE_DEGREES ||
    metrics.maxTieSpacingMeters > TIE_SPACING + 0.00001
  )
    throw new Error('Track render continuity validation failed.');
}

export class TrackVisual {
  readonly root = new THREE.Group();
  readonly chunkRevisions: number[];
  private chunks: TrackChunkBuild[];
  private pickTargets: readonly SectionPickTarget[];
  private readonly emphasisRoot = new THREE.Group();
  private renderBatchRoot = new THREE.Group();
  private readonly hoverMaterial = new THREE.MeshBasicMaterial({
    color: 0x7ef5e0,
    transparent: true,
    opacity: 0.72,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    toneMapped: false,
  });
  private readonly selectedMaterial = new THREE.MeshBasicMaterial({
    color: 0xffd27a,
    transparent: true,
    opacity: 0.9,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
    toneMapped: false,
  });
  private selectedSectionIds = new Set<string>();
  private hoveredSectionIds = new Set<string>();
  private proceduralSectionHardwareVisible = true;
  private revision = 0;
  private metrics: TrackContinuityMetrics;

  constructor(
    private plan: CompiledProjectTrack,
    private readonly materials: CoasterMaterialSet,
    private supportContext: TrackSupportContext = EMPTY_SUPPORT_CONTEXT,
    private readonly visualQuality: TrackVisualQuality = DEFAULT_TRACK_VISUAL_QUALITY,
  ) {
    this.root.name = 'generated-track';
    this.chunks = plan.spans.map((span) =>
      makeChunk(plan, span, materials, supportContext, visualQuality),
    );
    this.pickTargets = this.buildPickTargets(plan);
    this.chunkRevisions = plan.spans.map(() => this.revision);
    this.metrics = continuityMetrics(plan, this.chunks);
    assertContinuity(this.metrics);
    this.emphasisRoot.name = 'track-section-emphasis';
    this.renderBatchRoot = createTrackRenderBatches(this.chunks);
    this.root.add(
      ...this.chunks.map((chunk) => chunk.root),
      this.renderBatchRoot,
      this.emphasisRoot,
    );
  }

  rebuild(plan: CompiledProjectTrack, supportContext = this.supportContext): number {
    this.revision += 1;
    this.emphasisRoot.clear();
    // Rotation-minimizing frames are transported around the full closed loop.
    // A local edit can rotate downstream rail rings even where positions do not
    // change, so closed tracks are validated and swapped as one atomic candidate.
    const rebuildAll = plan.sampler.closed || plan.spans.length !== this.chunks.length;
    const affected = rebuildAll
      ? plan.spans.map((span) => span.index)
      : [...new Set(plan.changedSpanIndices)];
    const staged = new Map<number, TrackChunkBuild>();
    try {
      for (const index of affected)
        staged.set(
          index,
          makeChunk(plan, plan.spans[index]!, this.materials, supportContext, this.visualQuality),
        );
      const candidate = plan.spans.map((_, index) => staged.get(index) ?? this.chunks[index]!);
      const metrics = continuityMetrics(plan, candidate);
      assertContinuity(metrics);

      for (const [index, next] of staged) {
        const previous = this.chunks[index];
        if (previous) this.root.remove(previous.root);
        this.chunks[index] = next;
        this.chunkRevisions[index] = this.revision;
        this.root.add(next.root);
        if (previous) disposeGroup(previous.root);
      }
      for (const stale of this.chunks.slice(plan.spans.length)) {
        this.root.remove(stale.root);
        disposeGroup(stale.root);
      }
      this.chunks.splice(plan.spans.length);
      this.chunkRevisions.splice(plan.spans.length);
      this.plan = plan;
      this.supportContext = supportContext;
      this.pickTargets = this.buildPickTargets(plan);
      this.metrics = metrics;
      this.root.remove(this.renderBatchRoot);
      disposeGroup(this.renderBatchRoot);
      this.renderBatchRoot = createTrackRenderBatches(this.chunks);
      this.root.add(this.renderBatchRoot);
      this.applyProceduralSectionHardwareVisibility();
      this.rebuildEmphasis();
      return affected.length;
    } catch (error) {
      staged.forEach((chunk) => disposeGroup(chunk.root));
      throw error;
    }
  }

  continuity(): TrackContinuityMetrics {
    return { ...this.metrics };
  }

  sectionPickTargets(): readonly SectionPickTarget[] {
    return this.pickTargets;
  }

  productionSupportDistances(): readonly number[] {
    return this.chunks.flatMap((chunk) => {
      const supportRoot = chunk.root.getObjectByName('track-supports');
      const distances = supportRoot?.userData.productionSupportDistances;
      return Array.isArray(distances)
        ? distances.filter((distance): distance is number => Number.isFinite(distance))
        : [];
    });
  }

  setProceduralSectionHardwareVisible(visible: boolean): void {
    if (this.proceduralSectionHardwareVisible === visible) return;
    this.proceduralSectionHardwareVisible = visible;
    this.applyProceduralSectionHardwareVisibility();
  }

  private applyProceduralSectionHardwareVisibility(): void {
    this.root.traverse((object) => {
      if (object.userData.proceduralSectionHardware === true)
        object.visible = this.proceduralSectionHardwareVisible;
    });
  }

  setSectionEmphasis(selectedSectionId: string | null, hoveredSectionId: string | null): void {
    this.setPieceEmphasis(
      selectedSectionId ? [selectedSectionId] : [],
      hoveredSectionId ? [hoveredSectionId] : [],
    );
  }

  setPieceEmphasis(
    selectedSectionIds: readonly string[],
    hoveredSectionIds: readonly string[],
  ): void {
    const nextSelected = new Set(selectedSectionIds);
    const nextHovered = new Set(hoveredSectionIds);
    if (
      nextSelected.size === this.selectedSectionIds.size &&
      nextHovered.size === this.hoveredSectionIds.size &&
      [...nextSelected].every((id) => this.selectedSectionIds.has(id)) &&
      [...nextHovered].every((id) => this.hoveredSectionIds.has(id))
    )
      return;
    this.selectedSectionIds = nextSelected;
    this.hoveredSectionIds = nextHovered;
    this.rebuildEmphasis();
  }

  dispose(): void {
    this.emphasisRoot.clear();
    disposeGroup(this.root);
    this.hoverMaterial.dispose();
    this.selectedMaterial.dispose();
  }

  private buildPickTargets(plan: CompiledProjectTrack): readonly SectionPickTarget[] {
    return plan.spans.flatMap((span) => {
      const midpoint = span.startDistance + (span.endDistance - span.startDistance) * 0.5;
      const sectionId = plan.sampler.sampleByDistance(midpoint).section?.id;
      if (!sectionId) return [];
      const segmentCount = THREE.MathUtils.clamp(
        Math.ceil((span.endDistance - span.startDistance) / 2),
        1,
        15,
      );
      const points = Array.from({ length: segmentCount + 1 }, (_, index) => {
        const distance = THREE.MathUtils.lerp(
          span.startDistance,
          span.endDistance,
          index / segmentCount,
        );
        return vector(plan.sampler.sampleByDistance(distance).position);
      });
      return [{ sectionId, points }];
    });
  }

  private rebuildEmphasis(): void {
    this.emphasisRoot.clear();
    const addSection = (sectionId: string | null, material: THREE.Material, label: string) => {
      if (!sectionId) return;
      for (const chunk of this.chunks) {
        if (String(chunk.root.userData.sectionId) !== sectionId) continue;
        for (const side of ['left', 'right'] as const) {
          const source = chunk.rails[side].mesh;
          const overlay = new THREE.Mesh(source.geometry, material);
          overlay.name = `${label}:${sectionId}:${side}`;
          overlay.position.copy(source.position);
          overlay.quaternion.copy(source.quaternion);
          overlay.scale.copy(source.scale);
          overlay.renderOrder = 90;
          overlay.raycast = () => undefined;
          this.emphasisRoot.add(overlay);
        }
      }
    };
    for (const sectionId of this.hoveredSectionIds)
      if (!this.selectedSectionIds.has(sectionId))
        addSection(sectionId, this.hoverMaterial, 'hovered-track-piece');
    for (const sectionId of this.selectedSectionIds)
      addSection(sectionId, this.selectedMaterial, 'selected-track-piece');
  }
}

export class TrainVisual {
  readonly root = new THREE.Group();
  private readonly cars: THREE.Group[];
  private readonly ownedMaterials: THREE.Material[];
  private productionCars: readonly THREE.Group[] = [];
  private productionCarLods: Array<0 | 1 | 2> = [];
  private productionMiddleInstances: Array<{
    mesh: THREE.InstancedMesh;
    lod: 0 | 1 | 2;
  }> = [];

  constructor(material: THREE.Material, carCount: number) {
    const dark = new THREE.MeshStandardMaterial({
      color: 0x18242c,
      roughness: 0.56,
      metalness: 0.38,
    });
    const seat = new THREE.MeshStandardMaterial({ color: 0x243b48, roughness: 0.72 });
    const restraint = new THREE.MeshStandardMaterial({
      color: 0xffd66e,
      roughness: 0.38,
      metalness: 0.18,
    });
    this.ownedMaterials = [dark, seat, restraint];
    this.root.name = 'steel-train-rig';
    this.root.userData.assetContract = {
      familyId: 'steel.standard',
      anchors: ['frontSeatCamera', 'chaseTarget', 'passengerCam', 'reactionCamSurface'],
      occluders: ['carBody'],
    };
    this.cars = Array.from({ length: carCount }, (_, index) => {
      const car = new THREE.Group();
      car.name = `train-car:${index}`;
      car.userData.carIndex = index;
      const chassis = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.22, 2.25), dark);
      chassis.position.y = 0.24;
      chassis.castShadow = true;
      const body = new THREE.Mesh(new THREE.BoxGeometry(1.62, 0.66, 2.08), material);
      body.name = 'carBody';
      body.position.y = 0.62;
      body.castShadow = true;
      body.receiveShadow = true;
      const nose = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.34, 0.52), material);
      nose.position.set(0, 0.77, -1.05);
      nose.rotation.x = -0.12;
      const seatBack = new THREE.Mesh(new THREE.BoxGeometry(1.25, 0.78, 0.22), seat);
      seatBack.position.set(0, 1.12, 0.28);
      seatBack.rotation.x = -0.1;
      const lapBar = new THREE.Mesh(
        new THREE.TorusGeometry(0.48, 0.055, 6, 14, Math.PI),
        restraint,
      );
      lapBar.position.set(0, 1.02, -0.28);
      lapBar.rotation.set(Math.PI / 2, 0, Math.PI / 2);
      const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.72, 8), dark);
      axle.rotation.z = Math.PI / 2;
      axle.position.set(0, 0.05, 0.18);
      car.add(chassis, body, nose, seatBack, lapBar, axle);
      for (const z of [-0.62, 0.62]) {
        for (const x of [-0.82, 0.82]) {
          const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.12, 10), dark);
          wheel.rotation.z = Math.PI / 2;
          wheel.position.set(x, 0.03, z);
          wheel.castShadow = true;
          car.add(wheel);
        }
      }
      const frontSeatCamera = new THREE.Object3D();
      frontSeatCamera.name = 'anchor:front-seat-camera';
      frontSeatCamera.position.set(0, 1.55, -0.75);
      const chaseTarget = new THREE.Object3D();
      chaseTarget.name = 'anchor:chase-target';
      chaseTarget.position.set(0, 0.95, 0);
      const passengerCam = new THREE.Object3D();
      passengerCam.name = 'anchor:passenger-cam';
      passengerCam.position.set(0, 1.35, 0.25);
      const reactionSurface = new THREE.Object3D();
      reactionSurface.name = 'anchor:reaction-cam-surface';
      reactionSurface.position.set(0, 1.45, -0.12);
      car.add(frontSeatCamera, chaseTarget, passengerCam, reactionSurface);
      this.root.add(car);
      return car;
    });
  }

  place(sampler: TrackSampler, referenceDistance: number, spacing: number): void {
    const basis = new THREE.Matrix4();
    this.cars.forEach((car, index) => {
      const sample = sampler.sampleByDistance(referenceDistance - index * spacing);
      basis.makeBasis(vector(sample.right), vector(sample.up), vector(sample.tangent));
      car.position.copy(vector(sample.position)).addScaledVector(vector(sample.up), 0.52);
      car.quaternion.setFromRotationMatrix(basis);
    });
    this.syncProductionMiddleInstances();
  }

  placeFrame(frame: SimulationFrame): void {
    const basis = new THREE.Matrix4();
    this.cars.forEach((car, index) => {
      const pose = frame.cars[index];
      if (!pose) return;
      basis.makeBasis(vector(pose.right), vector(pose.up), vector(pose.forward));
      car.position.copy(vector(pose.position)).addScaledVector(vector(pose.up), 0.52);
      car.quaternion.setFromRotationMatrix(basis);
    });
    this.syncProductionMiddleInstances();
  }

  installProductionCars(productionCars: readonly THREE.Group[]): void {
    this.productionCars.forEach((root) => root.removeFromParent());
    this.productionCars = productionCars.slice(0, this.cars.length);
    this.cars.forEach((car, index) => {
      const production = this.productionCars[index];
      if (!production) return;
      for (const child of car.children) {
        if (child instanceof THREE.Mesh) child.visible = false;
      }
      production.name = `production-train-car:${index}`;
      batchStaticMeshHierarchy(production, () => 'car');
      car.add(production);
    });
    this.installProductionMiddleInstances();
  }

  setProductionCarLod(index: number, lod: 0 | 1 | 2): void {
    this.productionCarLods[index] = lod;
    this.syncProductionMiddleInstances();
  }

  private installProductionMiddleInstances(): void {
    this.productionMiddleInstances.forEach(({ mesh }) => mesh.removeFromParent());
    this.productionMiddleInstances = [];
    this.productionCarLods = this.productionCars.map((root) => {
      for (const object of root.getObjectByName('production-static-batches')?.children ?? []) {
        const match = /__LOD([012])$/.exec(object.name);
        if (match && object.visible) return Number(match[1]) as 0 | 1 | 2;
      }
      return 0;
    });
    const middleRoots = this.productionCars.slice(1);
    const referenceBatch = middleRoots[0]?.getObjectByName('production-static-batches');
    if (!referenceBatch || middleRoots.length < 2) return;
    referenceBatch.children.forEach((object, batchIndex) => {
      if (!(object instanceof THREE.Mesh)) return;
      const lodMatch = /__LOD([012])$/.exec(object.name);
      if (!lodMatch) return;
      const sources = middleRoots.map(
        (root) => root.getObjectByName('production-static-batches')?.children[batchIndex],
      );
      if (!sources.every((source) => source instanceof THREE.Mesh)) return;
      const mesh = new THREE.InstancedMesh(object.geometry, object.material, middleRoots.length);
      mesh.name = `production-middle-cars:${object.name}`;
      mesh.castShadow = object.castShadow;
      mesh.receiveShadow = object.receiveShadow;
      mesh.raycast = () => undefined;
      sources.forEach((source) => {
        source.visible = false;
        source.name = `${source.name}::instanced-source`;
      });
      this.root.add(mesh);
      this.productionMiddleInstances.push({
        mesh,
        lod: Number(lodMatch[1]) as 0 | 1 | 2,
      });
    });
    this.syncProductionMiddleInstances();
  }

  private syncProductionMiddleInstances(): void {
    if (!this.productionMiddleInstances.length) return;
    const activeLod = Math.min(...this.productionCarLods.slice(1)) as 0 | 1 | 2;
    const matrix = new THREE.Matrix4();
    this.productionMiddleInstances.forEach(({ mesh, lod }) => {
      mesh.visible = lod === activeLod;
      this.cars.slice(1).forEach((car, index) => {
        car.updateMatrix();
        const production = this.productionCars[index + 1];
        production?.updateMatrix();
        matrix.copy(car.matrix);
        if (production) matrix.multiply(production.matrix);
        mesh.setMatrixAt(index, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
    });
  }

  setProductionColor(color: THREE.ColorRepresentation): void {
    const next = new THREE.Color(color);
    this.productionCars.forEach((root) => {
      root.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials)
          if (material.name === 'M_Coaster_CreatorPrimary' && 'color' in material)
            (material as THREE.MeshStandardMaterial).color.copy(next);
      });
    });
  }

  dispose(): void {
    this.productionMiddleInstances.forEach(({ mesh }) => mesh.removeFromParent());
    this.productionMiddleInstances = [];
    this.productionCars.forEach((root) => disposeStaticMeshBatches(root));
    this.productionCars.forEach((root) => root.removeFromParent());
    this.productionCars = [];
    disposeGroup(this.root);
    this.ownedMaterials.forEach((owned) => owned.dispose());
  }
}
